import { useCallback, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  updateOrganization,
  type TOrganization,
  type TSlugChange,
} from '../../entities/organization';

export interface IOrganizationDraft {
  name: string;
  slug: string;
}

const SLUG = /^[a-z0-9]+(-[a-z0-9]+)*$/;

export const useOrganizationProfile = (organization: TOrganization) => {
  const client = useQueryClient();
  const saved = useMemo<IOrganizationDraft>(
    () => ({ name: organization.name, slug: organization.slug }),
    [organization],
  );
  const [draft, setDraft] = useState(saved);
  const [edited, setEdited] = useState(false);
  const [moved, setMoved] = useState<TSlugChange | null>(null);

  const current = edited ? draft : saved;

  const change = useCallback((patch: Partial<IOrganizationDraft>) => {
    setDraft((previous) => ({ ...previous, ...patch }));
    setEdited(true);
  }, []);

  const nameOk = current.name.trim().length > 0 && current.name.trim().length <= 120;
  const slugOk = SLUG.test(current.slug.trim()) && current.slug.trim().length >= 3;
  const dirty = current.name !== saved.name || current.slug !== saved.slug;

  const save = useMutation({
    mutationFn: () => updateOrganization({ name: current.name.trim(), slug: current.slug.trim() }),
    onSuccess: async (result) => {
      setMoved(result.slugChange ?? null);
      setEdited(false);
      await client.invalidateQueries({ queryKey: ['organization'] });
    },
  });

  return {
    draft: current,
    change,
    dirty,
    nameOk,
    slugOk,
    canSave: dirty && nameOk && slugOk,
    save,
    moved,
  };
};

export type TOrganizationProfile = ReturnType<typeof useOrganizationProfile>;
